import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MoveHorizontal } from "lucide-react";
import { api } from "@/lib/api";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function BeforeAfter({ item }) {
  const [pos, setPos] = useState(50);
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  const onDown = () => { dragging.current = true; };
  const onUp = () => { dragging.current = false; };
  const onMove = (e) => {
    if (!dragging.current) return;
    move(e.touches ? e.touches[0].clientX : e.clientX);
  };

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="before-after"
      className="relative w-full aspect-[4/3] overflow-hidden select-none cursor-ew-resize border border-white/10"
      onMouseDown={(e) => { onDown(); move(e.clientX); }}
      onTouchStart={(e) => { onDown(); move(e.touches[0].clientX); }}
    >
      <img src={item.image} alt={`${item.title} — after`} className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img
          src={item.before_image}
          alt={`${item.title} — before`}
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: wrapRef.current ? wrapRef.current.getBoundingClientRect().width : "100%" }}
          draggable={false}
        />
      </div>
      <span className="absolute top-3 left-3 bg-black/70 text-white text-[10px] tracking-widest uppercase px-2.5 py-1">Before</span>
      <span className="absolute top-3 right-3 bg-brand-orange text-white text-[10px] tracking-widest uppercase px-2.5 py-1">After</span>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none" style={{ left: `${pos}%` }}>
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white text-brand-ink flex items-center justify-center shadow-lg">
          <MoveHorizontal size={18} />
        </span>
      </div>
    </div>
  );
}

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  const pairs = items.filter((i) => i.before_image);
  const singles = items.filter((i) => !i.before_image);

  return (
    <section id="gallery" className="relative bg-brand-surface py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-brand-orange" />
              <span className="text-xs md:text-sm tracking-[0.25em] uppercase font-heading text-brand-orange">
                Our work
              </span>
            </div>
            <h2 className="font-heading font-medium tracking-tight text-white text-4xl md:text-6xl leading-[1.05]">
              Real jobs, real results.
            </h2>
          </div>
          <p className="text-white/60 font-body max-w-sm">
            Drag the sliders to see the transformation, and tap any photo to enlarge. Every job across the North East.
          </p>
        </div>

        {pairs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-testid="gallery-pairs">
            {pairs.map((g, i) => (
              <motion.div
                key={g.id}
                data-testid={`gallery-pair-${g.id}`}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08 }}
              >
                <BeforeAfter item={g} />
                <p className="text-white font-heading text-lg mt-3">{g.title}</p>
              </motion.div>
            ))}
          </div>
        )}

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" data-testid="gallery-grid">
          {singles.map((g, i) => (
            <motion.button
              key={g.id}
              data-testid={`gallery-item-${g.id}`}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.06 }}
              onClick={() => setActive(g)}
              className="group relative block w-full mb-5 overflow-hidden border border-white/10 text-left"
            >
              <img
                src={g.image}
                alt={g.title}
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="absolute bottom-0 left-0 right-0 p-5 text-white font-heading translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                {g.title}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-brand-orange"
              onClick={() => setActive(null)}
              aria-label="Close"
              data-testid="gallery-lightbox-close"
            >
              <X size={30} />
            </button>
            <motion.div
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img src={active.image} alt={active.title} className="w-full max-h-[80vh] object-contain" />
              <p className="text-white font-heading text-lg mt-4 text-center">{active.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
